import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useClickOutside from "../../hooks/useClickOutside";
import { Box, Stack, Typography } from "@mui/material";
import "./styles/NoActivityAttachments.scss";
import { getService } from "../../services/axios/services";
import { getProjectAttachmentsWithoutActivities as fetchProjectAttachmentsWithoutActivities } from "../../services/api/activities/activities.api";
import ActivitiesContext from "../../context/activitiesContext";
import AppContext from "../../context/appContext";
import TableLoading from "../TableLoading/TableLoading";
import ContentItem from "./ContentItem/ContentItem";
import { ContentAttachmentsResults } from "../ContentListAttachments/ContentListAttachments";
import ContentPagination from "../ContentPagination/ContentPagination";
import ListCount from "../ListCount/ListCount";
import ReorderButton from "../ReorderButton/ReorderButton";
import Filter from "./Filter/Filter";
import FilterIcon from "../VectorIcons/FilterIcon";

const NoActivityAttachments = (props) => {
  const {
    slug,
    onSelectionChange,
    isDisabled: isAreaDisabled,
    hasGlobalSelection,
    clearSelectionsTrigger,
    onActivityCreated,
  } = props;

  const activitiesContext = useContext(ActivitiesContext);
  const appContext = useContext(AppContext);

  const [state, setState] = useState({ loading: true, data: [] });
  const [stateFilterFormat, setStateFilterFormat] = useState([]);
  const [stateShowFormats, setShowFormats] = useState(false);
  const [activeContent, setActiveContent] = useState(null);
  const [stateFilter, setStateFilter] = useState({
    Origins: [1, 2],
    OnlyWithoutActivities: true,
  });
  const [stateOrderDirection, setStateOrderDirection] = useState("");
  const [formatDramaturgy, setFormatDramaturgy] = useState({
    loading: true,
    list: [],
  });
  const [formatVarieties, setFormatVarieties] = useState({
    loading: true,
    list: [],
  });

  // Ref keeps activeContent readable inside stable callbacks without adding it as a dep
  const activeContentRef = useRef(activeContent);
  activeContentRef.current = activeContent;

  const formatsFilterRef = useRef(null);

  const slugType = useMemo(
    () => activitiesContext.remakeSlugType(slug),
    [activitiesContext, slug]
  );

  const setLoading = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));
  }, []);

  const getFormats = useCallback((endpoint, setter) => {
    getService({ endpoint }).then((format) => {
      setter((prev) => ({ ...prev, list: format.data, loading: false }));
    });
  }, []);

  const getProjectAttachmentsWithoutActivities = useCallback(() => {
    fetchProjectAttachmentsWithoutActivities(stateFilter)
      .then((res) => {
        setState((prev) => ({ ...prev, ...res, loading: false }));
      })
      .catch(() => {
        setState((prev) => ({ ...prev, loading: false }));
      });
  }, [stateFilter]);

  const getDramaturgyAndVarietiesFormats = useCallback(() => {
    getFormats("dramaturgies/thesaurus?Origins=22", setFormatDramaturgy);
    getFormats("varieties/thesaurus?Origins=22", setFormatVarieties);
  }, [getFormats]);

  // Re-fetch attachments when slug or active filter changes
  useEffect(() => {
    setLoading();
    getProjectAttachmentsWithoutActivities();
  }, [slug, stateFilter, setLoading, getProjectAttachmentsWithoutActivities]);

  // Fetch format lists once on mount
  useEffect(() => {
    getDramaturgyAndVarietiesFormats();
  }, [getDramaturgyAndVarietiesFormats]);

  useClickOutside(formatsFilterRef, () => setShowFormats(false));

  const handleContentSelectionChange = useCallback(
    (index, hasSelection, isValid, selectedCount) => {
      if (hasSelection) {
        const info = { index, isValid, selectedCount };
        activeContentRef.current = info;
        setActiveContent(info);
        if (onSelectionChange) onSelectionChange(info);
      } else if (activeContentRef.current?.index === index) {
        activeContentRef.current = null;
        setActiveContent(null);
        if (onSelectionChange) onSelectionChange(null);
      }
    },
    [onSelectionChange]
  );

  const setFilter = useCallback((filter, format) => {
    if (format !== undefined) setStateFilterFormat(format);
    setStateFilter(filter);
  }, []);

  const handlePageChange = useCallback(
    (page) => {
      setLoading();
      setFilter({ ...stateFilter, pageNumber: page.pageNumber });
    },
    [setLoading, setFilter, stateFilter]
  );

  const orderByReceivedAt = useCallback(() => {
    setStateOrderDirection((prevDirection) => {
      const newDirection = prevDirection === 0 ? 1 : 0;
      setStateFilter((prevFilter) => ({
        ...prevFilter,
        "OrderBy.Column": "ReceivedAt",
        "OrderBy.Direction": newDirection,
      }));
      return newDirection;
    });
  }, []);

  const getIdFormats = useCallback((id) => {
    setStateFilterFormat((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  }, []);

  const sendFilterFormat = useCallback(() => {
    setShowFormats(false);
    setFilter({ ...stateFilter, FormatIds: stateFilterFormat, pageNumber: 1 });
  }, [setFilter, stateFilter, stateFilterFormat]);

  const toggleShowFormats = useCallback(() => {
    setShowFormats((prev) => !prev);
  }, []);

  const noAttachmentsFoundMessage = `Nenhum anexo sem atividade encontrado para o(s) formato(s) selecionado(s)`;

  return (
    <Box className="noActivityAttachments">
      <Typography
        component="span"
        className="noActivityTitle"
        sx={{ fontFamily: "inherit" }}
      >
        Anexos sem atividades
      </Typography>

      <>
        {!!state.data.length && (
          <Stack direction="row" alignItems="center" className="listCounters">
            <ListCount
              total={state.pagingInfo.extraTotals?.totalAttachments}
              text="Anexos"
            />
            <Box component="span" className="counterSeparator">
              |
            </Box>
            <ListCount total={state.pagingInfo.totalItems} text="Projetos" />
          </Stack>
        )}

        <ContentAttachmentsResults
          results={!state.loading ? state.data : []}
          showHeader={!state.loading}
          disabledHeader={hasGlobalSelection}
          detailsProps={{ ref: formatsFilterRef }}
          lastAttachmentProps={{ onClick: orderByReceivedAt }}
          lastAttachmentHeader={
            <ReorderButton
              text="Último anexo"
              color="#3898CD"
              direction={stateOrderDirection}
            />
          }
          detailsHeader={
            <>
              <Box component="span" onClick={toggleShowFormats}>
                Formatos <FilterIcon />
              </Box>
              {stateShowFormats && (
                <Filter
                  dramaturgy={formatDramaturgy}
                  varieties={formatVarieties}
                  isDramaturgy
                  isVarieties
                  stateFilterFormat={stateFilterFormat}
                  getIdFormats={getIdFormats}
                  loading={state.loading}
                  sendFilterFormat={sendFilterFormat}
                />
              )}
            </>
          }
          renderResult={(file, index) => (
            <ContentItem
              key={file.id ?? index}
              appContext={appContext}
              currentFiles={file.attachments}
              currentProject={file}
              contentType={file.contentType}
              context={appContext}
              index={index}
              getFiles={getProjectAttachmentsWithoutActivities}
              type={slugType.label}
              setLoading={setLoading}
              onSelectionChange={handleContentSelectionChange}
              isDisabled={
                isAreaDisabled ||
                (activeContent !== null && activeContent.index !== index)
              }
              hasGlobalSelection={hasGlobalSelection}
              clearSelectionsTrigger={clearSelectionsTrigger}
              onActivityCreated={onActivityCreated}
            />
          )}
        >
          {state.loading && <TableLoading />}

          {state.data.length === 0 && !state.loading && (
            <Box className="noAttachmentsFound">{noAttachmentsFoundMessage}</Box>
          )}
        </ContentAttachmentsResults>
      </>

      {!!state.pagingInfo && state.pagingInfo.totalPages > 1 && (
        <Box
          className={`containerPagination${hasGlobalSelection ? " disabled" : ""}`}
        >
          <Box className="container">
            <ContentPagination
              totalItems={state.pagingInfo.totalItems}
              itemsPerPage={state.pagingInfo.itemsPerPage}
              currentPage={state.pagingInfo.currentPage}
              prevPage={state.pagingInfo.previousPage}
              nextPage={state.pagingInfo.nextPage}
              totalPages={state.pagingInfo.totalPages}
              handleChange={handlePageChange}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NoActivityAttachments;
